/**
 * 「내 PC 에만 저장」 DB 연결의 금고 — 접속 정보(호스트·계정·비밀번호…)를 **이 브라우저에만** 암호화해 둔다.
 *
 * 왜: 고객정보처럼 민감한 DB 의 접속 정보를 서버에 두지 않으려고(서버 services/local_conns). 서버는 이름·종류만 안다.
 *
 * 암호화: AES-GCM 256. 키는 이 브라우저가 처음 한 번 만들고 **꺼낼 수 없게(extractable: false)** IndexedDB 에 둔다.
 * 그래서 브라우저 저장소를 파일로 열어 봐도 평문이 보이지 않고, 저장소를 다른 PC 로 복사해도 키를 쓸 수 없다.
 * 같은 PC·같은 브라우저 프로필에서 이 앱(같은 주소)으로만 풀린다. 브라우저 데이터를 지우면 값도 사라진다 — 다시 넣는다.
 *
 * 항목은 로그인 아이디별로 나눈다 — 한 PC 를 여러 사람이 써도 남의 연결 값을 쓰지 않는다.
 */

const DB_NAME = 'openflow-vault';
const ENTRIES = 'entries';
const KEYS = 'keys';
const KEY_ID = 'aes-gcm-v1';

export interface PcConnValues {
  conn_type: string;
  host: string;
  port: number | null;
  login: string;
  password: string;
  schema: string;
  extra: Record<string, any>;
}

interface Sealed { iv: Uint8Array; data: ArrayBuffer; at: string }

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('indexedDB unavailable')); return; }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(ENTRIES)) db.createObjectStore(ENTRIES);
      if (!db.objectStoreNames.contains(KEYS)) db.createObjectStore(KEYS);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  return open().then((db) => new Promise<T>((resolve, reject) => {
    const t = db.transaction(store, mode);
    const r = fn(t.objectStore(store));
    r.onsuccess = () => resolve(r.result as T);
    r.onerror = () => reject(r.error);
    t.oncomplete = () => db.close();
  }));
}

let keyPromise: Promise<CryptoKey> | null = null;

/** 이 브라우저의 금고 키 — 없으면 만든다(꺼낼 수 없는 키) */
function key(): Promise<CryptoKey> {
  if (!keyPromise) {
    keyPromise = (async () => {
      const found = await tx<CryptoKey | undefined>(KEYS, 'readonly', (s) => s.get(KEY_ID));
      if (found) return found;
      const k = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
      await tx(KEYS, 'readwrite', (s) => s.put(k, KEY_ID));
      return k;
    })();
    keyPromise.catch(() => { keyPromise = null; });
  }
  return keyPromise;
}

const slot = (user: string, id: string) => `${user}::${id}`;

export function vaultAvailable(): boolean {
  return typeof indexedDB !== 'undefined' && typeof crypto !== 'undefined' && !!crypto.subtle;
}

export async function putPcConn(user: string, id: string, values: PcConnValues): Promise<void> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, await key(), new TextEncoder().encode(JSON.stringify(values)));
  const sealed: Sealed = { iv, data, at: new Date().toISOString() };
  await tx(ENTRIES, 'readwrite', (s) => s.put(sealed, slot(user, id)));
}

/** 이 PC 에 값이 없거나 풀 수 없으면 null */
export async function getPcConn(user: string, id: string): Promise<PcConnValues | null> {
  try {
    const sealed = await tx<Sealed | undefined>(ENTRIES, 'readonly', (s) => s.get(slot(user, id)));
    if (!sealed) return null;
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: sealed.iv }, await key(), sealed.data);
    return JSON.parse(new TextDecoder().decode(plain)) as PcConnValues;
  } catch {
    return null;
  }
}

export async function removePcConn(user: string, id: string): Promise<void> {
  await tx(ENTRIES, 'readwrite', (s) => s.delete(slot(user, id)));
}

/** 이 PC 에 값이 있는 연결 이름들 */
export async function listPcConnIds(user: string): Promise<string[]> {
  try {
    const keys = await tx<IDBValidKey[]>(ENTRIES, 'readonly', (s) => s.getAllKeys());
    const prefix = `${user}::`;
    return keys.map(String).filter((k) => k.startsWith(prefix)).map((k) => k.slice(prefix.length));
  } catch {
    return [];
  }
}
